import '@material/mwc-list';
import '@material/mwc-list/mwc-list-item.js';
import { useEffect, useState, view } from '@esui/core';
import { css } from 'lit';
import { html } from 'lit';
import { Item } from '../autocomplete/index.js';
import { Chip } from '../chip/index.js';
import { debounce } from '../util/debounce.js';

export interface FilterItem {
  key: Item;
  value: Item;
}

export class FilterInputProps {
  data: Item[] = [];
  onSearch?: (text: string, keyId?: string) => void = undefined;
  onChange?: (values: FilterItem[], isValid: boolean) => void = undefined;
  required = false;
  placeholder = 'Filter';
}

function template({
  data,
  required,
  placeholder,
  onSearch,
  onChange,
}: FilterInputProps) {
  const [values, setValues] = useState<FilterItem[]>([]);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);

  const [searchText, _setSearchText] = useState<string>('');
  const setSearchText = debounce(_setSearchText);

  const [selectedKey, setSelectedKey] = useState<Item | undefined>(undefined);

  const [input, setInput] = useState<HTMLInputElement | undefined>(undefined);

  useEffect(element => {
    const inputEl: any = element.shadowRoot?.getElementById('input');
    setInput(inputEl);
  }, []);

  useEffect(() => {
    if (!focus) {
      if (open) {
        setTimeout(() => setOpen(false), 100);
      }
      return;
    }

    if (searchText.includes(':')) {
      const [keyName, text] = searchText.split(':').map(v => v.trim());
      if (!selectedKey) {
        const key = data.find(
          ({ name }) => name.toLowerCase() === keyName.toLocaleLowerCase()
        );
        setSelectedKey(key);
        onSearch?.(text, key?.id);
      } else {
        onSearch?.(text, selectedKey.id);
      }
    } else {
      if (selectedKey) {
        setSelectedKey(undefined);
      }
      onSearch?.(searchText);
    }
    input?.focus();
  }, [searchText, selectedKey, focus, open]);

  useEffect(() => {
    if (data && data.length > 0 && focus) {
      setOpen(true);
    } else {
      setTimeout(() => {
        setOpen(false);
      }, 100);
    }
  }, [data, focus]);

  useEffect(() => {
    onChange?.(values, !required || values.length > 0);
  }, [values]);

  return html`
    <div class="top-container">
      <div class="container">
        ${values.map(
          value =>
            Chip(`${value.key.name}: ${value.value.name}`).onAction(() => {
              setValues(values => values.filter(v => v !== value));
              setFocus(true);
            }).body
        )}
        <input
          id="input"
          class="input"
          name="filter"
          placeholder="${placeholder}"
          .value="${searchText}"
          @input="${(e: any) => setSearchText(e.target.value)}"
          @focus="${() => setFocus(true)}"
          @blur="${() => {
            setFocus(false);
          }}"
        />
      </div>
      <mwc-list
        id="list"
        class="content"
        ?open="${open}"
        @action="${(e: any) => {
          const value = data[e.detail.index];
          if (!selectedKey) {
            setSelectedKey(value);
            setSearchText(`${value.name}: `);
          } else {
            setValues(values => [...values, { key: selectedKey, value }]);
            setSelectedKey(undefined);
            setSearchText('');
          }
          setFocus(true);
        }}"
      >
        ${data.map(
          ({ name: text }) => html`<mwc-list-item>${text}</mwc-list-item>`
        )}
      </mwc-list>
    </div>
  `;
}

export const cssTemplate = css`
  .top-container {
    position: relative;
    border: solid 1px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    padding: 0 0.5rem;
    background-color: var(--mdc-theme-background, inherit);
  }

  .container {
    display: flex;
    flex-wrap: wrap;
  }

  .container > * {
    margin: 0.25rem 0;
  }

  .container > *:not(:first-child) {
    margin-left: 0.5rem;
  }

  .input {
    border: none;
    outline: none;
    border-radius: 4px;
    padding: 0 0.5rem;
    flex-grow: 1;
    font-size: inherit;
    min-height: 2rem;
    background-color: var(--mdc-theme-background, inherit);
  }

  .content {
    visibility: hidden;
    opacity: 0;
    transition: all 0.1s;
    position: absolute;
    margin-top: 4px;
    left: 0;
    right: 0;
    top: 100;
    z-index: 100;
    background-color: var(--mdc-theme-background, #ffffff);
    color: var(--mdc-theme-text-primary-on-background, inherit);
    box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.08);
    border: solid 1px rgba(0, 0, 0, 0.08);
    border-radius: 0 0 4px 4px;
  }

  .content[open] {
    visibility: visible;
    opacity: 1;
    max-height: 15rem;
    overflow-y: auto;
  }

  mwc-list-item {
    padding: 0 16px;
  }
`;

export const [FilterField] = view('jsui-material-filter-field', {
  template,
  cssTemplate,
  Props: FilterInputProps,
});
